import {
  ImportValidationError,
  UnsupportedImportVersionError,
} from "@/domain";

import {
  marginaliaExportV1Schema,
  marginaliaLibraryExportV1Schema,
  type MarginaliaExportV1,
  type MarginaliaLibraryExportV1,
} from "../schemas/v1";

function describeIssues(
  issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>,
): string[] {
  return issues.map((issue) => {
    const path = issue.path.length === 0 ? "root" : issue.path.join(".");
    return `${path}: ${issue.message}`;
  });
}

export function migrateDocumentExportToCurrent(
  value: unknown,
): MarginaliaExportV1 {
  if (typeof value !== "object" || value === null) {
    throw new ImportValidationError(
      "The selected file does not contain a JSON object.",
    );
  }

  const descriptor = value as {
    format?: unknown;
    formatVersion?: unknown;
  };

  if (
    typeof descriptor.format === "string" &&
    descriptor.format !== "marginalia-annotations"
  ) {
    throw new UnsupportedImportVersionError(
      descriptor.format,
      typeof descriptor.formatVersion === "number"
        ? descriptor.formatVersion
        : null,
    );
  }

  if (descriptor.format !== "marginalia-annotations") {
    throw new ImportValidationError(
      "The selected file is not a Marginalia annotation export.",
    );
  }

  if (descriptor.formatVersion !== 1) {
    throw new UnsupportedImportVersionError(
      "marginalia-annotations",
      typeof descriptor.formatVersion === "number"
        ? descriptor.formatVersion
        : null,
    );
  }

  const migrated = marginaliaExportV1Schema.safeParse(value);
  if (!migrated.success) {
    const issues = describeIssues(migrated.error.issues);
    throw new ImportValidationError(
      "The Marginalia annotation export is invalid.",
      { issues, cause: migrated.error },
    );
  }

  // Parse a second time after the (currently identity) migration. Future
  // migrations normalize into this value before this final validation.
  return marginaliaExportV1Schema.parse(migrated.data);
}

export function migrateLibraryExportToCurrent(
  value: unknown,
): MarginaliaLibraryExportV1 {
  if (typeof value !== "object" || value === null) {
    throw new ImportValidationError(
      "The selected file does not contain a JSON object.",
    );
  }

  const descriptor = value as {
    format?: unknown;
    formatVersion?: unknown;
  };

  if (
    typeof descriptor.format === "string" &&
    descriptor.format !== "marginalia-library"
  ) {
    throw new UnsupportedImportVersionError(
      descriptor.format,
      typeof descriptor.formatVersion === "number"
        ? descriptor.formatVersion
        : null,
    );
  }

  if (descriptor.format !== "marginalia-library") {
    throw new ImportValidationError(
      "The selected file is not a Marginalia library export.",
    );
  }

  if (descriptor.formatVersion !== 1) {
    throw new UnsupportedImportVersionError(
      "marginalia-library",
      typeof descriptor.formatVersion === "number"
        ? descriptor.formatVersion
        : null,
    );
  }

  const migrated = marginaliaLibraryExportV1Schema.safeParse(value);
  if (!migrated.success) {
    const issues = describeIssues(migrated.error.issues);
    throw new ImportValidationError(
      "The Marginalia library export is invalid.",
      { issues, cause: migrated.error },
    );
  }

  return marginaliaLibraryExportV1Schema.parse(migrated.data);
}
